import type { FormEvent, ReactNode } from 'react';

import AdminCatalogImageField from './AdminCatalogImageField';
import FiliereContenuPedagogiqueEditor from './FiliereContenuPedagogiqueEditor';
import FiliereTarifsEditor from './FiliereTarifsEditor';
import AcademiaSelect from '../../common/AcademiaSelect';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import type { FiliereContenuPedagogique } from '../../../utils/filiereContenuPedagogique';
import type { FiliereTarifsDraft } from '../../../utils/filiereTarifsDraft';

export type FiliereFormValues = {
  code: string;
  libelle: string;
  domaine: string;
  description: string;
  urlImage: string;
  ordre: string;
  estActive: boolean;
  contenuPedagogique: FiliereContenuPedagogique;
  tarifs: FiliereTarifsDraft;
};

type DomaineOption = { code: string; libelle: string };

type Props = {
  mode: 'create' | 'edit';
  values: FiliereFormValues;
  domaineOptions: DomaineOption[];
  domaineEmpty?: boolean;
  onChange: (patch: Partial<FiliereFormValues>) => void;
  onSubmit: (event: FormEvent) => void;
  onCancel?: () => void;
  isSaving?: boolean;
  onUploadError?: (message: string) => void;
};

const fieldClass =
  'academia-field w-full rounded-xl border border-outline-variant/25 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm transition focus:border-primary/40 focus:ring-2 focus:ring-primary/15';

function FieldLabel({
  children,
  hint,
  required,
}: {
  children: ReactNode;
  hint?: string;
  required?: boolean;
}) {
  return (
    <span className="mb-1.5 block">
      <span className="text-xs font-bold uppercase tracking-wide text-slate-700">
        {children}
        {required ? <span className="text-primary"> *</span> : null}
      </span>
      {hint ? (
        <span className="mt-0.5 block text-[11px] font-normal normal-case text-on-surface-variant">{hint}</span>
      ) : null}
    </span>
  );
}

function SectionHeading({ icon, title, description }: { icon: string; title: string; description: string }) {
  return (
    <div className="flex gap-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <LucideSymbolIcon name={icon} tone="primary" size={20} />
      </span>
      <div className="min-w-0">
        <h4 className="text-sm font-black text-slate-900">{title}</h4>
        <p className="mt-0.5 text-xs leading-relaxed text-on-surface-variant">{description}</p>
      </div>
    </div>
  );
}

export default function FiliereCatalogForm({
  mode,
  values,
  domaineOptions,
  domaineEmpty = false,
  onChange,
  onSubmit,
  onCancel,
  isSaving = false,
  onUploadError,
}: Props) {
  const isCreate = mode === 'create';
  const domaineLabel =
    domaineOptions.find((d) => d.code.toUpperCase() === values.domaine.toUpperCase())?.libelle ?? values.domaine;

  return (
    <form
      onSubmit={onSubmit}
      className="min-w-0 overflow-hidden rounded-2xl border border-outline-variant/20 bg-white shadow-sm ring-1 ring-slate-900/[0.03]"
    >
      <div className="border-b border-outline-variant/15 bg-gradient-to-r from-primary/[0.06] via-white to-white px-5 py-4 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">
              {isCreate ? 'Nouvelle filière' : 'Modification'}
            </p>
            <h3 className="mt-1 text-base font-black text-slate-900">
              {isCreate ? 'Ajouter un parcours au catalogue' : values.libelle || 'Filière'}
            </h3>
            {!isCreate && values.code ? (
              <p className="mt-1 font-mono text-xs text-on-surface-variant">{values.code}</p>
            ) : null}
          </div>
          {!isCreate ? (
            <span className="inline-flex max-w-[12rem] items-center gap-2 rounded-xl border border-outline-variant/15 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm">
              <LucideSymbolIcon name="category" tone="primary" size={16} />
              <span className="truncate">{domaineLabel || 'Domaine'}</span>
            </span>
          ) : null}
        </div>
      </div>

      <div className="grid min-w-0 gap-0 lg:grid-cols-5">
        <div className="min-w-0 space-y-5 border-b border-outline-variant/10 p-5 sm:p-6 lg:col-span-2 lg:border-b-0 lg:border-r">
          <SectionHeading
            icon="school"
            title="Référentiel formation"
            description="Identité technique, domaine parent et ordre d’affichage dans le catalogue."
          />

          {isCreate ? (
            <label className="block min-w-0">
              <FieldLabel required hint="Code unique en majuscules (ex. DEV_WEB).">
                Code filière
              </FieldLabel>
              <input
                required
                className={`${fieldClass} font-mono uppercase`}
                value={values.code}
                onChange={(e) => onChange({ code: e.target.value.toUpperCase() })}
                placeholder="DEV_WEB"
              />
            </label>
          ) : null}

          <label className="block min-w-0">
            <FieldLabel required>Libellé affiché</FieldLabel>
            <input
              required
              className={fieldClass}
              value={values.libelle}
              onChange={(e) => onChange({ libelle: e.target.value })}
              placeholder="Développement web full-stack"
            />
          </label>

          <label className="block min-w-0">
            <FieldLabel required hint="Famille de filières sur la vitrine.">
              Domaine de formation
            </FieldLabel>
            <AcademiaSelect
              required
              className="mt-0 w-full"
              value={values.domaine}
              disabled={domaineEmpty}
              onChange={(e) => onChange({ domaine: e.target.value })}
            >
              {domaineEmpty ? (
                <option value="" disabled>
                  Créez d’abord un domaine (onglet Domaines)
                </option>
              ) : (
                <>
                  {isCreate && !values.domaine ? (
                    <option value="" disabled>
                      Choisir un domaine…
                    </option>
                  ) : null}
                  {domaineOptions.map((d) => (
                    <option key={d.code} value={d.code}>
                      {d.libelle}
                    </option>
                  ))}
                </>
              )}
            </AcademiaSelect>
          </label>

          <label className="block min-w-0">
            <FieldLabel hint="Position dans les listes du domaine.">Ordre d’affichage</FieldLabel>
            <input
              type="number"
              className={fieldClass}
              value={values.ordre}
              onChange={(e) => onChange({ ordre: e.target.value })}
            />
          </label>

          {!isCreate ? (
            <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-outline-variant/20 bg-surface-container-low/60 px-4 py-3">
              <div>
                <span className="text-sm font-semibold text-slate-900">Filière active</span>
                <p className="text-xs text-on-surface-variant">Visible sur la vitrine publique et l’app mobile.</p>
              </div>
              <input
                type="checkbox"
                className="h-5 w-5 rounded border-outline-variant/40 text-primary focus:ring-primary/30"
                checked={values.estActive}
                onChange={(e) => onChange({ estActive: e.target.checked })}
              />
            </label>
          ) : null}
        </div>

        <div className="min-w-0 space-y-5 bg-gradient-to-br from-slate-50/90 via-white to-primary/[0.03] p-5 sm:p-6 lg:col-span-3">
          <SectionHeading
            icon="public"
            title="Présentation vitrine"
            description="Texte et visuel de la fiche formation sur le web et le mobile."
          />

          <label className="block min-w-0">
            <FieldLabel hint="Résumé affiché sur la carte et la page détail formation.">
              Texte descriptif
            </FieldLabel>
            <textarea
              className={`${fieldClass} min-h-[110px] resize-y leading-relaxed`}
              value={values.description}
              onChange={(e) => onChange({ description: e.target.value })}
              rows={4}
              placeholder="Objectifs, public visé, compétences visées, modalités…"
            />
          </label>

          <AdminCatalogImageField
            value={values.urlImage}
            onChange={(urlImage) => onChange({ urlImage })}
            inputClass={fieldClass}
            onUploadError={onUploadError}
          />
        </div>
      </div>

      <div className="border-t border-outline-variant/10 px-5 py-5 sm:px-6">
        <FiliereContenuPedagogiqueEditor
          value={values.contenuPedagogique}
          onChange={(contenuPedagogique) => onChange({ contenuPedagogique })}
        />
      </div>

      <div className="border-t border-outline-variant/10 px-5 py-5 sm:px-6">
        {isCreate ? (
          <p className="rounded-xl border border-dashed border-outline-variant/30 bg-surface-container-low/50 px-4 py-3 text-sm text-on-surface-variant">
            Les tarifs personnalisés de cette formation pourront être définis juste après sa création (modification
            du parcours).
          </p>
        ) : (
          <FiliereTarifsEditor
            value={values.tarifs}
            onChange={(tarifs) => onChange({ tarifs })}
            disabled={isSaving}
          />
        )}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-outline-variant/15 bg-surface-container-low/40 px-5 py-4 sm:px-6">
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-outline-variant/30 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
          >
            Annuler
          </button>
        ) : null}
        <button
          type="submit"
          disabled={isSaving || (isCreate && domaineEmpty)}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-primary/20 hover:opacity-95 disabled:opacity-50"
        >
          <LucideSymbolIcon name={isCreate ? 'add' : 'save'} tone="inherit" size={18} className="text-white" />
          {isSaving ? 'Enregistrement…' : isCreate ? 'Créer la filière' : 'Enregistrer'}
        </button>
      </div>
    </form>
  );
}
